import { useState, useEffect, useContext } from "react";
import axiosInstance from "../utils/axiosInstance";
import FormCard from "../components/FormCard";
import { ProtectedComponent } from "../components/ProtectedComponent";
import Table from "../components/Table";
import { MdOutlineEdit } from "react-icons/md";
import { FaUserCheck } from "react-icons/fa";
import ValidateUserModal from "../components/ValidateUserModal";
import { AiOutlineUserDelete } from "react-icons/ai";
import { Button } from "flowbite-react";
import { RiUserAddLine } from "react-icons/ri";
import DeleteModal from "../components/DeleteModal";
import { UserContext } from "../utils/UserContext";
import UserEditModal from "./UserModal";

export function UserManagement() {
  const { user: currentUser } = useContext(UserContext);

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const [isValidateUserModalOpen, setIsValidateUserModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  function updateUsers() {
    axiosInstance
      .get("/v51/users")
      .then((res) => {
        setUsers(res.data);
      })
      .catch((e) => {
        setError(e.response.data.message);
        console.log(e);
      });
  }
  useEffect(updateUsers, []);

  const openEditUserModal = (user) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const openValidateUserModal = (user) => {
    setSelectedUser(user);
    setIsValidateUserModalOpen(true);
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const openAddUserModal = () => {
    setSelectedUser(null);
    setIsAddModalOpen(true);
  };

  const handleEditUser = (user) => {
    if (user.name === "" || user.email === "" || user.company === "") {
      setError("Please fill in all the fields");
      return;
    }
    axiosInstance
      .put(`/v51/users/${user.id}`, user)
      .then((res) => {
        setSelectedUser(null);
        setIsEditModalOpen(false);
        updateUsers();
      })
      .catch((e) => {
        setError(e.response?.data?.message);
      });
  };

  const handleAddUser = (user) => {
    if (user.name === "" || user.email === "" || user.company === "") {
      setError("Please fill in all the fields");
      return;
    }
    axiosInstance
      .post("/v51/users", user)
      .then((res) => {
        setIsAddModalOpen(false);
        updateUsers();
      })
      .catch((e) => {
        setError(e.response.data.message);
        console.log(e);
      });
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setIsAddModalOpen(false);
  };

  const handleValidateUser = (company) => {
    if (!company) {
      setError("Please select a company");
      return;
    }
    if (!selectedUser) {
      setError("Invalid user");
      return;
    }
    axiosInstance
      .patch(`/v51/users/${encodeURIComponent(selectedUser.email)}/validate`, {
        email: selectedUser.email,
        companyId: company.value,
      })
      .then((res) => {
        setIsValidateUserModalOpen(false);
        updateUsers();
      })
      .catch((e) => {
        setError(e.response?.data?.message);
        console.log(e);
      });
  };

  const handleUserDelete = (user) => {
    axiosInstance
      .delete(`/v51/users/${encodeURIComponent(user.email)}`)
      .then((res) => {
        setIsDeleteModalOpen(false);
        updateUsers();
      })
      .catch((e) => {
        setError(e.response?.data?.message);
        console.log(e);
      });
  };

  return (
    <ProtectedComponent role={"ADMIN"}>
      <section className="container mx-auto flex flex-col items-center justify-center overflow-x-auto lg:w-9/12 lg:py-0">
        <div className="-mx-4 flex flex-wrap">
          <div className="w-full px-4">
            <div className="mx-auto mb-[60px] max-w-[520px] text-center lg:mb-20">
              <span className="text-primary mb-2 block text-lg font-semibold dark:text-white"></span>
              <h2 className="text-dark mb-4 text-3xl font-bold dark:text-white sm:text-[40px]/[48px]">
                User Management
              </h2>
            </div>
          </div>
        </div>
        <FormCard>
          <Table columns={getColumns()} data={users} />
        </FormCard>
        <Button onClick={openAddUserModal} color="blue">
          <RiUserAddLine className="my-auto mr-2" />
          Add New User{" "}
        </Button>

        {isValidateUserModalOpen && (
          <ValidateUserModal
            enabled={isValidateUserModalOpen}
            user={selectedUser}
            onClose={() => setIsValidateUserModalOpen(false)}
            handleSubmit={handleValidateUser}
            error={error}
          />
        )}
        {isEditModalOpen && (
          <UserEditModal
            user={selectedUser}
            onClose={handleCloseModal}
            onSave={handleEditUser}
            error={error}
          />
        )}
        {isAddModalOpen && (
          <UserEditModal
            user={null}
            onClose={handleCloseModal}
            onSave={handleAddUser}
            error={error}
          />
        )}
        {selectedUser && (
          <DeleteModal
            message={`Are you sure you want to delete ${selectedUser.email}?`}
            show={isDeleteModalOpen}
            onClose={() => setSelectedUser(null)}
            onDelete={() => handleUserDelete(selectedUser)}
          />
        )}
      </section>
    </ProtectedComponent>
  );
  function getButton(user) {
    if (user.validated) {
      return (
        <Button onClick={() => openEditUserModal(user)} color="blue" size="xs">
          <MdOutlineEdit className="my-auto mr-2" />
          Edit
        </Button>
      );
    } else {
      return (
        <Button
          onClick={() => openValidateUserModal(user)}
          color="green"
          size="xs"
        >
          <FaUserCheck className="mr-1" />
          Validate user
        </Button>
      );
    }
  }
  function getColumns() {
    return [
      {
        key: "edit",
        name: "Edit",
        content: (user) => getButton(user),
        type: "dynamic",
      },
      {
        key: "email",
        name: "Email",
      },
      {
        key: "firstName",
        name: "First Name",
      },
      {
        key: "lastName",
        name: "Last Name",
      },
      {
        key: "company",
        name: "Company",
      },
      {
        key: "jobTitle",
        name: "Job Title",
      },
      {
        key: "role",
        name: "Role",
      },
      {
        key: "active",
        name: "Active",
        type: "boolean",
      },
      {
        key: "validated",
        name: "Validated",
        type: "boolean",
      },
      {
        key: "confirmed",
        name: "Confirmed",
        type: "boolean",
      },
      {
        key: "lastLogin",
        name: "Last Login",
        type: "date",
      },
      {
        key: "registrationDate",
        name: "Registration Date",
        type: "date",
      },
      {
        key: "delete",
        name: "Delete",
        content: (user) => (
          <Button
            color="failure"
            size={"xs"}
            onClick={() => handleDelete(user)}
            disabled={currentUser.email === user.email}
          >
            <AiOutlineUserDelete className="my-auto mr-2" />
            Delete
          </Button>
        ),
        type: "dynamic",
      },
    ];
  }
}
